import {
  Box,
  Button,
  Collapse,
  Divider,
  Icon,
  OsStack,
  Typography,
} from "@outschool/backpack";
import { TextMimeType } from "@outschool/gql-frontend-generated";
import { faClock } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  AttachmentGallery,
  TextWithLinks,
} from "@outschool/ui-components-shared";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import { RichTextView } from "@outschool/ui-rich-text-editor";
import React, { FC, useCallback, useRef, useState } from "react";
import { Player } from "video.js";

import { useFormatDuration } from "../../../hooks/useFormatDuration";
import { POST_CONTENT_TRUNCATE_LENGTH } from "../../../lib/constants";
import OutschoolVideoJsAdapter from "../../VideoJs/OutschoolVideoJsAdapter";
import { MessageContent, Sender } from "../types";

const VIDEO_TIME_UPDATE_INTERVAL_MS = 5000;

interface VideoWithEventsProps {
  src: string;
  onVideoTimeUpdate?: (currentTime: number, duration: number) => void;
  onVideoEnded?: () => void;
}

function VideoWithEvents({
  src,
  onVideoTimeUpdate,
  onVideoEnded,
}: VideoWithEventsProps) {
  const callbacksRef = useRef({ onVideoTimeUpdate, onVideoEnded });
  callbacksRef.current = { onVideoTimeUpdate, onVideoEnded };
  const lastEmitRef = useRef(0);

  const handlePlayerReady = useCallback((player: Player) => {
    player.on("timeupdate", () => {
      const now = Date.now();
      if (now - lastEmitRef.current < VIDEO_TIME_UPDATE_INTERVAL_MS) {
        return;
      }
      lastEmitRef.current = now;

      const currentTime = player.currentTime?.() ?? 0;
      const duration = player.duration?.() ?? 0;
      if (duration > 0) {
        callbacksRef.current.onVideoTimeUpdate?.(currentTime, duration);
      }
    });

    player.on("ended", () => {
      callbacksRef.current.onVideoEnded?.();
    });
  }, []);

  return (
    <Box sx={{ borderRadius: 16, overflow: "hidden" }}>
      <OutschoolVideoJsAdapter src={src} onPlayerReady={handlePlayerReady} />
    </Box>
  );
}

export interface ClassPostContentProps {
  sender: Sender;
  messageContent: MessageContent;
  trackingLabel?: string;
  trackingMetadata?: TrackingProperties;
  allowRichText?: boolean;
  /**
   * Height of the collapsed content in pixels.
   * @default 150
   */
  collapseSize?: number;
  estimatedDuration?: number | null;
  /** When true, the inline AttachmentGallery is hidden (caller renders attachments separately). */
  hideAttachments?: boolean;
  /** When true, content is never collapsed and "Show more" is hidden. */
  disableCollapse?: boolean;
  onVideoTimeUpdate?: (currentTime: number, duration: number) => void;
  onVideoEnded?: () => void;
}

const ClassPostContent: FC<ClassPostContentProps> = ({
  sender,
  messageContent,
  trackingLabel,
  trackingMetadata,
  allowRichText = false,
  collapseSize = 150,
  estimatedDuration,
  hideAttachments = false,
  disableCollapse = false,
  onVideoTimeUpdate,
  onVideoEnded,
}) => {
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\ClassPost\\ClassPostContent",
  );
  const formatDuration = useFormatDuration();
  const [userExpanded, setUserExpanded] = useState(false);

  const [contentRef, setContentRef] = React.useState<
    HTMLDivElement | HTMLPreElement | null
  >(null);
  const contentSize = contentRef?.scrollHeight;

  const textLength = messageContent.text?.length ?? 0;
  const htmlTextLength = messageContent.htmlText?.length ?? 0;
  const contentLength = Math.max(textLength, htmlTextLength);
  const isLongContent =
    !disableCollapse &&
    contentLength >= POST_CONTENT_TRUNCATE_LENGTH &&
    (!contentSize || contentSize > collapseSize);
  const expanded = userExpanded || !isLongContent;
  const showExpandButton = !expanded;

  const showEstimatedDuration =
    estimatedDuration != null && estimatedDuration > 0;

  const videoSrc =
    messageContent.video?.fileUrl ??
    messageContent.video?.file?.url ??
    messageContent.externalVideoUrl;
  const showVideo = !!videoSrc;

  return (
    <Box
      data-test-id="ClassPostContent"
      sx={{
        backgroundColor: "common.white",
        borderRadius: 16,
        paddingX: 32,
        paddingY: 20,
      }}
    >
      <OsStack spacing={5}>
        {showEstimatedDuration && (
          <>
            <OsStack direction="row" spacing={2} alignItems="center">
              <Icon
                icon={faClock}
                sx={{ fontSize: 20, color: "neutral.800" }}
              />
              <Typography variant="subtitle1">
                {formatDuration(estimatedDuration, showVideo)}
              </Typography>
            </OsStack>
            <Divider sx={{ borderColor: "neutral.100" }} />
          </>
        )}
        {showVideo && (
          <VideoWithEvents
            src={videoSrc}
            onVideoTimeUpdate={onVideoTimeUpdate}
            onVideoEnded={onVideoEnded}
          />
        )}
        <Box
          sx={{
            marginBottom: 40,
            position: "relative",
          }}
        >
          <Collapse
            in={expanded}
            collapsedSize={
              contentSize == null
                ? 0
                : contentSize < collapseSize
                ? contentSize
                : collapseSize
            }
          >
            {allowRichText &&
            messageContent.originalMimeType === TextMimeType.TextMarkdown ? (
              <RichTextView
                content={messageContent.htmlText}
                allowLinks={true}
                maxLines={Infinity}
                allowEmails={false}
                ref={setContentRef}
              />
            ) : (
              <Box
                component="pre"
                sx={{ wordBreak: "break-word", margin: 0 }}
                ref={setContentRef}
              >
                <TextWithLinks
                  clickableLinks={true}
                  text={messageContent.text?.trim() ?? ""}
                  maxTextLength={Infinity}
                  hideEmails={true}
                />
              </Box>
            )}
          </Collapse>
          {showExpandButton && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                position: "absolute",
                bottom: -24,
              }}
            >
              <Typography
                variant="subtitle1"
                color="textSecondary"
                sx={{ userSelect: "none" }}
              >
                ⋯
              </Typography>
              <Button
                onClick={() => setUserExpanded(true)}
                variant="link"
                size="small"
              >
                {t`Show more`}
              </Button>
            </Box>
          )}
        </Box>
      </OsStack>
      {!hideAttachments && (
        <AttachmentGallery
          video={messageContent.video}
          attachments={messageContent.attachments}
          trackingLabel={trackingLabel}
          trackingMetadata={trackingMetadata}
          senderName={sender.name}
          isPostOwner={false}
        />
      )}
    </Box>
  );
};

export default ClassPostContent;
